import { IRideMetadata, IStationInfo } from "../../dto";
import { IgnoreMLPathReasonEnum, WaypointCategoryEnum } from "../../enums";
import { IStationRouteDto, IStationRouteStationInfo } from "../../models";
import { isValidDateTime } from "../date.helpers";
import { getStationComposedId } from "../ride/ride-station-composed-id.helpers";
import { isRideStationsOrderSame } from "../ride/rides.helpers";
import {
  dropMlPathRoutingWaypoints,
  ensureRoutingWaypointsAreInterior,
  filterRealRouteStations,
  getRealRouteStationIds,
  haveRealRouteStationIdentitiesChanged,
  isMlPathRoutingWaypoint,
  isStationRouteWaypointStation,
  resolveRecountedStationsInfo,
  routeHasUserOwnedRoutingWaypoints,
  toEffectiveMlRouteIdsFromRideMetadata,
  toEffectiveMlRouteIdsFromRideRecount,
} from "../station-route";

/** Clears active saved ML path ids — ride edit shows gray "Save ML path" until re-saved. */
export const clearRideActiveMlPathRouteIds = (
  metadata: IRideMetadata,
): IRideMetadata => ({
  ...metadata,
  usedCachedRouteId: null,
  customStationRouteId: null,
});

/**
 * After dry-save when station structure changed (add/remove passenger station, etc.):
 * merge waypoints against pre-change stations, sync ML path ids, and clear the yellow
 * saved-path indicator when the real-station multiset changed (same as drag-reorder).
 */
export const buildRideMetadataAfterStationStructureDrySave = ({
  previousMetadata,
  drySaveMetadata,
}: {
  previousMetadata?: IRideMetadata | null;
  drySaveMetadata?: IRideMetadata | null;
}): IRideMetadata => {
  const previousStations = previousMetadata?.stationsInfo ?? [];
  const recountedStations = drySaveMetadata?.stationsInfo ?? [];
  const stationsMultisetChanged = haveRealRouteStationIdentitiesChanged({
    previousStations,
    nextStations: recountedStations,
  });

  const resolvedStationsInfo = resolveRecountedStationsInfo({
    recounted: recountedStations,
    source: previousStations,
    sourceRouteIds: toEffectiveMlRouteIdsFromRideMetadata(previousMetadata),
    recountRouteIds: toEffectiveMlRouteIdsFromRideRecount(drySaveMetadata),
  });

  let metadata = clearRideMlPathIgnoreIfNoUserOwnedWaypoints({
    metadata: {
      ...(drySaveMetadata ?? {}),
      stationsInfo: resolvedStationsInfo,
    },
    stations: resolvedStationsInfo,
  });

  metadata = applyRecountedMlPathRouteIds({
    metadata,
    customStationRouteId: drySaveMetadata?.customStationRouteId,
    usedCachedRouteId: drySaveMetadata?.usedCachedRouteId,
    shouldOptimize: stationsMultisetChanged,
  });

  if (stationsMultisetChanged) {
    return clearRideActiveMlPathRouteIds(metadata);
  }

  return metadata;
};

/**
 * After drag-reorder dry-save: keep the client visit order, drop purple MLPath W when the
 * new real-station order no longer matches the saved ML path, and restore the path when
 * the client order matches again.
 */
export const buildRideMetadataAfterDragReorder = ({
  clientReorderedMetadata,
  drySaveMetadata,
}: {
  clientReorderedMetadata?: IRideMetadata | null;
  drySaveMetadata?: IRideMetadata | null;
}): IRideMetadata => {
  const clientStations = ensureRoutingWaypointsAreInterior(
    dropMlPathRoutingWaypoints(clientReorderedMetadata?.stationsInfo ?? []),
  );
  const dryStationsFull = drySaveMetadata?.stationsInfo ?? [];
  const dryRealStations = filterRealRouteStations(dryStationsFull);
  const dryHasMlPath = Boolean(
    drySaveMetadata?.customStationRouteId ||
    dryStationsFull.some(isMlPathRoutingWaypoint),
  );
  const canRestoreMlPath =
    dryHasMlPath &&
    !haveRealRouteStationIdentitiesChanged({
      previousStations: clientStations,
      nextStations: dryStationsFull,
    }) &&
    isRideStationsOrderSame(clientStations, dryRealStations);

  if (canRestoreMlPath) {
    const stationsInfo = ensureRoutingWaypointsAreInterior(
      resolveRecountedStationsInfo({
        recounted: dryStationsFull,
        source: clientStations,
        sourceRouteIds: toEffectiveMlRouteIdsFromRideMetadata(
          clientReorderedMetadata,
        ),
        recountRouteIds: toEffectiveMlRouteIdsFromRideRecount(drySaveMetadata),
      }),
    );
    const orderMatchesDrySave = isRideStationsOrderSame(
      stationsInfo,
      dryStationsFull,
    );

    const restoredMetadata = clearRideMlPathIgnoreIfNoUserOwnedWaypoints({
      metadata: applyRecountedMlPathRouteIds({
        metadata: {
          ...(drySaveMetadata ?? {}),
          stationsInfo,
          stationIds: getRealRouteStationIds(stationsInfo),
          ...(orderMatchesDrySave
            ? {
                routePolyline: drySaveMetadata?.routePolyline,
                totalDuration: drySaveMetadata?.totalDuration,
                totalDistance: drySaveMetadata?.totalDistance,
                distances: drySaveMetadata?.distances,
                durations: drySaveMetadata?.durations,
              }
            : {
                routePolyline: undefined,
                distances: undefined,
                durations: undefined,
              }),
        },
        customStationRouteId: drySaveMetadata?.customStationRouteId,
        usedCachedRouteId: drySaveMetadata?.usedCachedRouteId,
        shouldOptimize: false,
      }),
      stations: stationsInfo,
    });

    return restoredMetadata;
  }

  const dryStations = dropMlPathRoutingWaypoints(dryStationsFull);
  const dryByComposedId = new Map(
    dryStations.map(station => [getStationComposedId(station), station]),
  );
  const stationsInfo = clientStations.map(station => {
    const recounted = dryByComposedId.get(getStationComposedId(station));

    if (!recounted?.arrivalTime) {
      return station;
    }

    return { ...station, arrivalTime: recounted.arrivalTime };
  });
  const orderMatchesDrySave = isRideStationsOrderSame(
    stationsInfo,
    dryStations,
  );

  return clearRideActiveMlPathRouteIds({
    ...(drySaveMetadata ?? {}),
    stationsInfo,
    stationIds: getRealRouteStationIds(stationsInfo),
    ...(orderMatchesDrySave
      ? {
          routePolyline: drySaveMetadata?.routePolyline,
          totalDuration: drySaveMetadata?.totalDuration,
          totalDistance: drySaveMetadata?.totalDistance,
          distances: drySaveMetadata?.distances,
          durations: drySaveMetadata?.durations,
        }
      : {
          routePolyline: undefined,
          distances: undefined,
          durations: undefined,
        }),
  });
};

export const rideHasActiveSavedMlPath = (
  metadata?: IRideMetadata | null,
): boolean =>
  Boolean(metadata?.customStationRouteId && !metadata?.ignoreMLPathReason);

export const rideHasIgnoredMlPath = (
  metadata?: IRideMetadata | null,
): boolean => Boolean(metadata?.ignoreMLPathReason);

/**
 * While ML path is ignored, purple MLPath W must not stay on the ride (including after
 * recount, which can still attach the saved path until Save persists ignore).
 */
export const stripIgnoredRideMlPathWaypoints = (
  metadata: IRideMetadata,
): IRideMetadata => {
  if (!metadata.ignoreMLPathReason || !metadata.stationsInfo) {
    return metadata;
  }

  const stationsInfo = ensureRoutingWaypointsAreInterior(
    dropMlPathRoutingWaypoints(metadata.stationsInfo),
  );

  return {
    ...metadata,
    stationsInfo,
    stationIds: getRealRouteStationIds(stationsInfo),
  };
};

/**
 * User-owned W changed the geometry: unbind the saved ML path and drop purple MLPath W
 * immediately so they do not wait for Save (orange ride W stay).
 */
export const applyRideMlPathIgnoredByWaypoints = (
  metadata: IRideMetadata,
): IRideMetadata => {
  const dismissedMlPathRouteId =
    metadata.customStationRouteId ?? metadata.usedCachedRouteId ?? null;

  return stripIgnoredRideMlPathWaypoints({
    ...metadata,
    customStationRouteId: null,
    usedCachedRouteId: dismissedMlPathRouteId,
    ignoreMLPathReason: IgnoreMLPathReasonEnum.WaypointsModified,
  });
};

/** ML path row to reopen/update after ignore — kept on usedCachedRouteId when dismissed. */
export const getRideDismissedMlPathStationRouteId = (
  metadata?: IRideMetadata | null,
): number | null => {
  if (!metadata?.ignoreMLPathReason) {
    return null;
  }

  return metadata.usedCachedRouteId ?? null;
};

export const getRideMlPathIgnoredTooltipKey = (
  reason?: IgnoreMLPathReasonEnum | null,
): string => {
  if (reason === IgnoreMLPathReasonEnum.UserDecision) {
    return "ride.SAVE_ROUTE.IGNORED_PATH_TOOLTIP_USER_DECISION";
  }

  if (reason === IgnoreMLPathReasonEnum.WaypointsModified) {
    return "ride.SAVE_ROUTE.IGNORED_PATH_TOOLTIP_WAYPOINTS";
  }

  return "ride.SAVE_ROUTE.IGNORED_PATH_TOOLTIP";
};

export const clearRideMlPathIgnoreReason = (
  metadata: IRideMetadata,
): IRideMetadata => {
  const { ignoreMLPathReason: _ignored, ...rest } = metadata;
  return {
    ...rest,
    ignoreMLPathReason: null,
  };
};

export const clearRideMlPathIgnoreIfNoUserOwnedWaypoints = ({
  metadata,
  stations,
}: {
  metadata: IRideMetadata;
  stations?: Parameters<typeof routeHasUserOwnedRoutingWaypoints>[0];
}): IRideMetadata => {
  const resolvedStations = stations ?? metadata.stationsInfo;

  if (
    metadata.ignoreMLPathReason !== IgnoreMLPathReasonEnum.WaypointsModified
  ) {
    return metadata;
  }

  if (routeHasUserOwnedRoutingWaypoints(resolvedStations)) {
    return metadata;
  }

  return clearRideMlPathIgnoreReason(metadata);
};

/**
 * Apply ML path ids from a recount response onto ride metadata.
 * Non-optimize recounts omit these ids on the BE — keep the ride's current selection
 * (e.g. right after Save ML Path) instead of clearing the indicator to gray.
 * Optimize recounts always replace (including null when no path was used).
 */
export const applyRecountedMlPathRouteIds = ({
  metadata,
  customStationRouteId,
  usedCachedRouteId,
  shouldOptimize,
}: {
  metadata: IRideMetadata;
  customStationRouteId?: number | null;
  usedCachedRouteId?: number | null;
  shouldOptimize?: boolean;
}): IRideMetadata => {
  if (metadata.ignoreMLPathReason) {
    return metadata;
  }

  const isNonOptimizeRecount = shouldOptimize === false;
  const hasRecountedCustomId =
    customStationRouteId !== null && customStationRouteId !== undefined;
  const hasRecountedCachedId =
    usedCachedRouteId !== null && usedCachedRouteId !== undefined;

  if (isNonOptimizeRecount && !hasRecountedCustomId && !hasRecountedCachedId) {
    return metadata;
  }

  return {
    ...metadata,
    customStationRouteId: isNonOptimizeRecount
      ? (customStationRouteId ?? metadata.customStationRouteId ?? null)
      : (customStationRouteId ?? null),
    usedCachedRouteId: isNonOptimizeRecount
      ? (usedCachedRouteId ?? metadata.usedCachedRouteId ?? null)
      : (usedCachedRouteId ?? null),
  };
};

const toRideMlPathWaypointFromStationRoute = ({
  routeStation,
  arrivalTime,
}: {
  routeStation: Pick<
    IStationRouteStationInfo,
    "id" | "name" | "locationInfo" | "areaId"
  >;
  arrivalTime?: TDateISO;
}): IStationInfo => ({
  id: routeStation.id,
  name: routeStation.name,
  locationInfo: routeStation.locationInfo,
  areaId: routeStation.areaId,
  waypointCategory: WaypointCategoryEnum.MLPathWaypoint,
  ...(isValidDateTime(arrivalTime) ? { arrivalTime } : {}),
});

/** Copy arrivalTime from previous/next neighbor so ride metadata stays valid for editors. */
export const fillMissingRideStationArrivalTimes = (
  stations: IStationInfo[],
): IStationInfo[] =>
  stations.map((station, index, allStations) => {
    if (isValidDateTime(station.arrivalTime)) {
      return station;
    }

    const previousWithTime = allStations
      .slice(0, index)
      .reverse()
      .find(candidate => isValidDateTime(candidate.arrivalTime));
    const nextWithTime = allStations
      .slice(index + 1)
      .find(candidate => isValidDateTime(candidate.arrivalTime));

    const arrivalTime =
      previousWithTime?.arrivalTime ?? nextWithTime?.arrivalTime;

    if (!isValidDateTime(arrivalTime)) {
      return station;
    }

    return { ...station, arrivalTime };
  });

const mapStationRouteStationsOntoRide = ({
  rideStations,
  routeStations,
}: {
  rideStations: IStationInfo[] | undefined;
  routeStations: IStationRouteStationInfo[];
}): IStationInfo[] => {
  const rideById = new Map(
    (rideStations ?? []).map(station => [station.id, station]),
  );
  const rideRealById = new Map(
    filterRealRouteStations(rideStations).map(station => [station.id, station]),
  );

  return routeStations.map(routeStation => {
    const existingRideStation = rideById.get(routeStation.id);

    if (isStationRouteWaypointStation(routeStation)) {
      return toRideMlPathWaypointFromStationRoute({
        routeStation,
        arrivalTime: existingRideStation?.arrivalTime,
      });
    }

    const rideStation = rideRealById.get(routeStation.id);
    if (rideStation) {
      return rideStation;
    }

    return {
      id: routeStation.id,
      name: routeStation.name,
      locationInfo: routeStation.locationInfo,
      areaId: routeStation.areaId,
      ...(existingRideStation &&
      isValidDateTime(existingRideStation.arrivalTime)
        ? { arrivalTime: existingRideStation.arrivalTime }
        : {}),
    };
  });
};

/**
 * After Save/Next on a customized ML path opened from a ride — apply that path's
 * station order onto the ride. Routing waypoints become MLPathWaypoint (purple W);
 * prior RideWaypoint rows are replaced by the path, not kept as ride-owned.
 * Arrival times are kept from the ride (or filled from neighbors) so editors stay valid.
 */
const mergeRideStationsMissingFromPath = ({
  rideStations,
  pathStations,
}: {
  rideStations: IStationInfo[] | undefined;
  pathStations: IStationInfo[];
}): IStationInfo[] => {
  const merged = [...pathStations];
  const pathRealIds = new Set(
    filterRealRouteStations(pathStations).map(station => station.id),
  );

  (rideStations ?? []).forEach((rideStation, index, allRideStations) => {
    if (
      isStationRouteWaypointStation(rideStation) ||
      pathRealIds.has(rideStation.id)
    ) {
      return;
    }

    const previousOnPath = [...allRideStations.slice(0, index)]
      .reverse()
      .find(candidate => pathRealIds.has(candidate.id));

    if (!previousOnPath) {
      let insertIndex = 0;
      while (
        insertIndex < merged.length &&
        !pathRealIds.has(merged[insertIndex].id) &&
        !isStationRouteWaypointStation(merged[insertIndex])
      ) {
        insertIndex += 1;
      }
      merged.splice(insertIndex, 0, rideStation);
      return;
    }

    const insertAt = merged.findIndex(
      station => station.id === previousOnPath.id,
    );
    merged.splice(insertAt >= 0 ? insertAt + 1 : merged.length, 0, rideStation);
  });

  return merged;
};

export const applySavedMlPathStationsToRideStations = ({
  rideStations,
  stationRoute,
}: {
  rideStations: IStationInfo[] | undefined;
  stationRoute: Pick<IStationRouteDto, "computedData" | "result"> | undefined;
}): IStationInfo[] => {
  const routeStations = stationRoute?.computedData?.stationInfos;
  if (routeStations?.length) {
    return fillMissingRideStationArrivalTimes(
      mergeRideStationsMissingFromPath({
        rideStations,
        pathStations: mapStationRouteStationsOntoRide({
          rideStations,
          routeStations,
        }),
      }),
    );
  }

  const orderedStationIds = stationRoute?.result?.orderedStationIds;
  const waypointStops = stationRoute?.result?.waypointStops ?? [];
  if (!orderedStationIds?.length) {
    return rideStations ?? [];
  }

  const rideById = new Map(
    (rideStations ?? []).map(station => [station.id, station]),
  );
  const rideRealById = new Map(
    filterRealRouteStations(rideStations).map(station => [station.id, station]),
  );
  const waypointById = new Map(
    waypointStops.map(waypoint => [waypoint.id, waypoint]),
  );

  return fillMissingRideStationArrivalTimes(
    mergeRideStationsMissingFromPath({
      rideStations,
      pathStations: orderedStationIds
        .map(stationId => {
          const waypoint = waypointById.get(stationId);
          if (waypoint) {
            return toRideMlPathWaypointFromStationRoute({
              routeStation: waypoint,
              arrivalTime: rideById.get(stationId)?.arrivalTime,
            });
          }

          return rideRealById.get(stationId);
        })
        .filter((station): station is IStationInfo => Boolean(station)),
    }),
  );
};
